import { colors, screen } from 'components/styles/variables';
import { useTranslation } from 'react-i18next';
import LabeledStarRatings from './LabeledStarRatings';
import PercentageBar from './PercentageBar';
import StarRating from './StarRating';

export interface AggregateCardProps {
  title: string;
  reviewCount: number;
  overallRating: number;
  breakdownByRatingPercentages: {
    fiveStar: number;
    fourStar: number;
    threeStar: number;
    twoStar: number;
    oneStar: number;
  };
  categoryAverages: Record<string, number>;
  color: 'gold' | 'red';
  writeReviewHref: string;
}

function calculateRatingPercentage(ratingCount: number, reviewCount: number): number {
  return Math.round((ratingCount / reviewCount) * 100);
}

export default function AggregateCard(props: AggregateCardProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="aggregate-card">
      <div className="header">{props.title}</div>
      <div className="overall-rating-container">
        <StarRating value={props.overallRating} color={props.color} />
        <div className="review-count">
          {`${props.reviewCount} ${t('seoSrp.reviews.aggregateCard.reviews')}`}
        </div>
      </div>
      <div className="stars-container">
        <PercentageBar
          color={props.color}
          label={`5 ${t('seoSrp.reviews.aggregateCard.star')}`}
          value={calculateRatingPercentage(
            props.breakdownByRatingPercentages.fiveStar,
            props.reviewCount
          )}
        />
        <PercentageBar
          color={props.color}
          label={`4 ${t('seoSrp.reviews.aggregateCard.star')}`}
          value={calculateRatingPercentage(
            props.breakdownByRatingPercentages.fourStar,
            props.reviewCount
          )}
        />
        <PercentageBar
          color={props.color}
          label={`3 ${t('seoSrp.reviews.aggregateCard.star')}`}
          value={calculateRatingPercentage(
            props.breakdownByRatingPercentages.threeStar,
            props.reviewCount
          )}
        />
        <PercentageBar
          color={props.color}
          label={`2 ${t('seoSrp.reviews.aggregateCard.star')}`}
          value={calculateRatingPercentage(
            props.breakdownByRatingPercentages.twoStar,
            props.reviewCount
          )}
        />
        <PercentageBar
          color={props.color}
          label={`1 ${t('seoSrp.reviews.aggregateCard.star')}`}
          value={calculateRatingPercentage(
            props.breakdownByRatingPercentages.oneStar,
            props.reviewCount
          )}
        />
      </div>
      <div className="divider" />
      <div className="ratings-container">
        <LabeledStarRatings ratings={props.categoryAverages} color={props.color} />
      </div>
      <div className="divider" />
      <a className="write-review" target="_blank" href={props.writeReviewHref}>
        {t('seoSrp.reviews.aggregateCard.writeAReview')}
      </a>

      <style jsx>{`
        .aggregate-card {
          border: 1px solid ${colors.G2};
          border-radius: 8px;
          width: 370px;
          display: flex;
          flex-direction: column;
          align-items: center;
          box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.08);
        }

        @media (min-width: ${screen.xsMax}) and (max-width: 849px) {
          .aggregate-card {
            width: 600px;
            margin-right: 32px;
          }
        }

        .header {
          font-weight: 700;
          font-size: 22px;
          line-height: 32px;
          text-align: center;
          letter-spacing: 0.2px;
          color: ${colors.BLACK};
          margin: 24px 0 10px 0;
          height: 32px;
        }

        .overall-rating-container {
          display: flex;
          flex-direction: column;
          margin-bottom: 42px;
          height: 26px;
        }

        .review-count {
          font-weight: 400;
          font-size: 14px;
          line-height: 20px;
          text-align: center;
          letter-spacing: 0.2px;
          color: ${colors.BLACK};
        }

        .ratings-container {
          padding: 16px 24px;
          width: 100%;
        }

        .stars-container {
          padding: 16px 24px;
          width: 100%;
          padding-bottom: 5px;
          display: flex;
          flex-direction: column;
        }

        .write-review {
          padding: 16px 0;
        }

        .divider {
          height: 1px;
          width: 322px;
          background-color: ${colors.G2};
        }

        @media (min-width: ${screen.xsMax}) and (max-width: 849px) {
          .divider {
            width: 552px;
          }
        }
      `}</style>
    </div>
  );
}
